import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import DateFnsUtils from "@date-io/date-fns";
import { Container, ThemeProvider } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import Amplify, { Auth } from "aws-amplify";
import { AUTH_TYPE, createAuthLink } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import lodash from "lodash";
import { SnackbarProvider } from "notistack";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import "./App.css";
import theme from "./assets/theme";
import awsconfig from "./aws-exports";
import NotFoundError from "./components/NotFoundError";
import ScrollToTop from "./components/ScrollToTop";
import { ROUTES } from "./constants";
import Authentication from "./navigation/Authentication";
import { ComanpyDetails, Companies, LocationDetails } from "./routes/Companies";
import Dashboard from "./routes/Dashboard";
import { EmployeeDetails, Employees } from "./routes/Employees";
import Layout from "./routes/Layout";
import SignIn from "./routes/SignIn";
import TestDetail from "./routes/TestDetail";
import Tests from "./routes/Tests";
import Users from "./routes/Users";

Amplify.configure(awsconfig);
const url = awsconfig.aws_appsync_graphqlEndpoint;
const region = awsconfig.aws_appsync_region;
const auth = {
  type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
  credentials: () => Auth.currentCredentials(),
  jwtToken: async () =>
    (await Auth.currentSession()).getIdToken().getJwtToken(),
};

const httpLink = createHttpLink({ uri: url });

const link = ApolloLink.from([
  // @ts-ignore
  createAuthLink({ url, region, auth }),
  // @ts-ignore
  createSubscriptionHandshakeLink(url, httpLink),
]);
const cache: InMemoryCache = new InMemoryCache({
  typePolicies: {
    User: {
      keyFields: ["username"],
    },
    Test: {
      keyFields: ["locationId", "testId", "date"],
    },
    Employee: {
      keyFields: ["locationId", "insuranceNumber"],
    },
    Company: {
      keyFields: ["id"],
      fields: {
        locations: {
          merge(existing, incoming) {
            let items: Array<any> = [];
            if (existing && existing.items) {
              items = items.concat(existing.items);
            }
            if (incoming && incoming.items) {
              items = items.concat(incoming.items);
            }
            return {
              ...existing,
              ...incoming,
              items: lodash.uniqWith(items, lodash.isEqual),
            };
          },
        },
      },
    },
    Query: {
      fields: {
        listEmployees: {
          keyArgs: ["locationId", "insuranceNumber"],
          merge(existing = {}, incoming = {}) {
            let items: Array<any> = [];
            if (existing && existing.items) {
              items = items.concat(existing.items);
            }
            if (incoming && incoming.items) {
              items = items.concat(incoming.items);
            }
            return {
              ...existing,
              ...incoming,
              items,
            };
          },
        },
        listCompanys: {
          merge(existing, incoming) {
            let items: Array<any> = [];
            if (existing && existing.items) {
              items = items.concat(existing.items);
            }
            if (incoming && incoming.items) {
              items = items.concat(incoming.items);
            }
            return {
              ...(existing || {}),
              ...(incoming || {}),
              items,
            };
          },
        },
      },
    },
  },
});

const client = new ApolloClient({
  link,
  cache,
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <ApolloProvider client={client}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <BrowserRouter>
              <ScrollToTop />
              <Switch>
                <Route path={ROUTES.LOGIN} exact>
                  <Container>
                    <SignIn />
                  </Container>
                </Route>

                <Authentication>
                  <Layout>
                    <Switch>
                      <Route path={"/"} exact>
                        <Redirect to={ROUTES.TESTS} />
                      </Route>
                      <Route path={ROUTES.DASHBOARD} exact>
                        <Dashboard />
                      </Route>
                      <Route path={ROUTES.COMPANIES} exact>
                        <Companies />
                      </Route>
                      <Route path={ROUTES.COMPANY} exact>
                        <ComanpyDetails />
                      </Route>
                      <Route path={ROUTES.LOCATION} exact>
                        <LocationDetails />
                      </Route>

                      <Route path={ROUTES.TESTS} exact>
                        <Tests />
                      </Route>
                      <Route path={ROUTES.TEST} exact>
                        <TestDetail />
                      </Route>
                      <Route path={ROUTES.EMPLOYEES} exact>
                        <Employees />
                      </Route>
                      <Route path={ROUTES.EMPLOYEE} exact>
                        <EmployeeDetails />
                      </Route>

                      <Route path={`${ROUTES.USERS}`} exact>
                        <Users />
                      </Route>
                      <Route path="*">
                        <NotFoundError />
                      </Route>
                    </Switch>
                  </Layout>
                </Authentication>
              </Switch>
            </BrowserRouter>
          </MuiPickersUtilsProvider>
        </ApolloProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
